import React, { useState, useMemo } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DumontLogo, Search, Plus, ArrowLeft, CloseX } from "../../../Generic/Icons";
import { ChevronUp } from "../../../Generic/Icons/ChevronUp";
import { FullscreenSidebar as StyledFullscreenSidebar } from "../../styles";
import { useConversations } from "../../hooks/useConversations";
import { useAvailableAgents } from "../../hooks/useAvailableAgents";
import { dumontColors } from "../../colors";
import {
  Header,
  LogoContainer,
  Title,
  CloseButton,
  SearchBarContainer,
  SearchInputWrapper,
  SearchIconContainer,
  StyledSearchField,
  ScrollableContent,
  NavigationSection,
  SectionLabel,
  SectionTitle,
  NavItem,
  NavItemText,
  SectionContainer,
  SectionHeader,
  SectionHeaderContent,
  SectionHeaderText,
  ItemsList,
  EmptyState,
  EmptyStateText,
  ChatItem,
  ChatTitle,
  ChatTime,
  AgentItem,
  AgentName,
  Footer,
  FooterButton,
  FooterButtonText,
} from "./styled";
import { FullScreenChat, FullScreenAgents, HelpIcon } from "Components/DumontSidebar/CustomIcons";

interface FullscreenSidebarProps {
  onClose: () => void;
  activeView?: "chat" | "history" | "agents";
  onHistory?: () => void;
  onNewChat?: () => void;
  onAgents?: () => void;
  onOpenConversation?: (conversationId: string) => void;
  onOpenAgentChat?: (agentId: string) => void;
  onHelpClick?: () => void;
  selectedAgentId?: string | null;
  activeConversationId?: string | null;
}

export default function FullscreenSidebar({
  onClose,
  activeView = "chat",
  onHistory,
  onNewChat,
  onAgents,
  onOpenConversation,
  onOpenAgentChat,
  onHelpClick,
  selectedAgentId,
  activeConversationId,
}: FullscreenSidebarProps) {
  const [chatsExpanded, setChatsExpanded] = useState(true);
  const [agentsExpanded, setAgentsExpanded] = useState(true);
  const { t: translate } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const { filterConversations, formatRelativeDate } = useConversations();
  const { agents: availableAgents } = useAvailableAgents();

  // Buscar conversas reais do histórico
  const conversations = useMemo(
    () => filterConversations(searchQuery),
    [filterConversations, searchQuery]
  );
  const recentChats = useMemo(
    () =>
      conversations
        .slice()
        .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())
        .slice(0, 5)
        .map((conv) => ({
          id: conv.id,
          title: conv.title,
          time: formatRelativeDate(conv.updatedAt),
        })),
    [conversations, formatRelativeDate]
  );

  // Filtrar chats e agents baseado na busca
  const filteredChats = recentChats.filter((chat) =>
    chat.title.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredAgents = availableAgents.filter((agent) =>
    agent.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <StyledFullscreenSidebar>
      {/* Header */}
      <Header>
        <LogoContainer>
          <DumontLogo size={32} />
          <Title>Dumont AI</Title>
        </LogoContainer>
        <CloseButton onClick={onClose}>
          <CloseX width={16} height={16} color={dumontColors.textTertiary} />
        </CloseButton>
      </Header>

      {/* Search Bar */}
      <SearchBarContainer>
        <SearchInputWrapper>
          <SearchIconContainer>
            <Search width={16} height={16} color={dumontColors.textTertiary} />
          </SearchIconContainer>
          <StyledSearchField
            fullWidth
            placeholder={translate("dumontSearchPlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            variant="standard"
          />
        </SearchInputWrapper>
      </SearchBarContainer>

      {/* Scrollable Content */}
      <ScrollableContent>
        {/* Navigation Section */}
        <NavigationSection>
          <SectionLabel>
            <SectionTitle>{translate("dumontNavigation")}</SectionTitle>
          </SectionLabel>
          <NavItem onClick={onNewChat} isActive={activeView === "chat"}>
            <Plus width={20} height={20} color={activeView === "chat" ? "#FFFFFF" : dumontColors.textTertiary} />
            <NavItemText isActive={activeView === "chat"}>{translate("dumontNewChat")}</NavItemText>
          </NavItem>
        </NavigationSection>

        {/* Chats Section */}
        <SectionContainer>
          <SectionHeader
            onClick={() => {
              if (onHistory) onHistory();
              setChatsExpanded(!chatsExpanded);
            }}
            isActive={activeView === "history"}
          >
            <SectionHeaderContent>
              <FullScreenChat
                sx={{
                  color: activeView === "history" ? "#FFFFFF" : dumontColors.textTertiary,
                  width: 20,
                  height: 20,
                }}
              />
              <SectionHeaderText isActive={activeView === "history"}>
                {translate("dumontChats")}
              </SectionHeaderText>
            </SectionHeaderContent>
            <Box
              sx={{
                transform: chatsExpanded ? "none" : "rotate(180deg)",
                display: "flex",
                alignItems: "center",
              }}
            >
              <ChevronUp
                width={16}
                height={16}
                color={activeView === "history" ? "#FFFFFF" : dumontColors.textTertiary}
              />
            </Box>
          </SectionHeader>

          {chatsExpanded && (
            <ItemsList>
              {recentChats.length === 0 ? (
                <EmptyState>
                  <EmptyStateText>
                    {searchQuery ? translate("dumontNoChatsFound") : translate("dumontNoRecentChats")}
                  </EmptyStateText>
                </EmptyState>
              ) : (
                recentChats.map((chat) => (
                  <ChatItem key={chat.id} onClick={() => onOpenConversation?.(chat.id)}>
                    <ChatTitle isActive={activeConversationId === chat.id}>{chat.title}</ChatTitle>
                    <ChatTime>{chat.time}</ChatTime>
                  </ChatItem>
                ))
              )}
            </ItemsList>
          )}
        </SectionContainer>

        {/* Agents Section */}
        <SectionContainer>
          <SectionHeader // will be enabled after 'create agents' is implemented
            // onClick={() => {
            //   if (onAgents) onAgents();
            //   setAgentsExpanded(!agentsExpanded);
            // }}
            isActive={activeView === "agents"}
          >
            <SectionHeaderContent>
              <FullScreenAgents
                sx={{
                  color: activeView === "agents" ? "#FFFFFF" : dumontColors.textTertiary,
                  width: 20,
                  height: 20,
                }}
              />
              <SectionHeaderText isActive={activeView === "agents"}>
                {translate("dumontAgents")}
              </SectionHeaderText>
              <Box
                component="span"
                sx={{
                  fontSize: 10,
                  fontWeight: 400,
                  fontStyle: "italic",
                  color: activeView === "agents" ? "rgba(255,255,255,0.7)" : dumontColors.textTertiary,
                  fontFamily: "Manrope",
                  ml: 0.5,
                }}
              >
                {translate("dumontComingSoon")}
              </Box>
            </SectionHeaderContent>
            {/* <Box
              sx={{
                transform: agentsExpanded ? "none" : "rotate(180deg)",
                display: "flex",
                alignItems: "center",
              }}
            >
              <ChevronUp
                width={16}
                height={16}
                color={activeView === "agents" ? "#FFFFFF" : dumontColors.textTertiary}
              />
            </Box> */}
          </SectionHeader>

          {/* {agentsExpanded && (
            <ItemsList>
              {filteredAgents.length === 0 && searchQuery ? (
                <EmptyState>
                  <EmptyStateText>{translate("dumontNoAgentsFound")}</EmptyStateText>
                </EmptyState>
              ) : (
                filteredAgents.map((agent) => (
                  <AgentItem key={agent.id} sx={{ cursor: "default", "&:hover": { backgroundColor: "transparent" } }}>
                    <AgentName isActive={selectedAgentId === agent.id}>{agent.name}</AgentName>
                  </AgentItem>
                ))
              )}
            </ItemsList>
          )} */}
        </SectionContainer>
      </ScrollableContent>

      {/* Footer */}
      <Footer>
        <FooterButton onClick={onClose}>
          <ArrowLeft width={20} height={20} color="#00A49F" />
          <FooterButtonText isPrimary>{translate("dumontReturnToShipeezi")}</FooterButtonText>
        </FooterButton>

        {/* <FooterButton onClick={onHelpClick}>
          <HelpIcon />
          <FooterButtonText>{translate("dumontHelp")}</FooterButtonText>
        </FooterButton> */}
      </Footer>
    </StyledFullscreenSidebar>
  );
}
